import { create } from "zustand";

/**
 * Dirty-state tracking for the authoring surfaces.
 *
 * This is the only client-owned state in the application. Everything with a
 * server-side truth lives in TanStack Query, so there is never a second copy of
 * a draft to reconcile.
 */
export type SaveStatus = "clean" | "dirty" | "saving" | "saved" | "error";

export interface DirtyState {
  status: SaveStatus;
  /** ISO timestamp of the last successful save, or null. */
  lastSavedAt: string | null;
  /** Operator-facing failure summary. Never clinical content. */
  errorMessage: string | null;

  markDirty: () => void;
  markSaving: () => void;
  markSaved: (savedAt: string) => void;
  markError: (message: string) => void;
  reset: () => void;
}

export const useDirtyState = create<DirtyState>((set) => ({
  status: "clean",
  lastSavedAt: null,
  errorMessage: null,

  markDirty: () => set({ status: "dirty", errorMessage: null }),
  markSaving: () => set({ status: "saving", errorMessage: null }),
  markSaved: (savedAt) =>
    set({ status: "saved", lastSavedAt: savedAt, errorMessage: null }),
  markError: (message) => set({ status: "error", errorMessage: message }),
  reset: () => set({ status: "clean", errorMessage: null }),
}));

export function describeSaveStatus(
  status: SaveStatus,
  lastSavedAt: string | null,
): string {
  switch (status) {
    case "clean":
      return lastSavedAt ? "All changes saved" : "No changes yet";
    case "dirty":
      return "Unsaved changes";
    case "saving":
      return "Saving…";
    case "saved":
      return "All changes saved";
    case "error":
      return "Save failed";
  }
}
